import moment, { Moment } from 'moment'

import BaseInputDate from 'app/components/forms/base_inputDate/component'
import * as helpers from 'app/sm/helpers'
import * as dateTimeHelpers from 'app/utils/datetime/helpers'

interface RentalReferenceInputDateProps {
  label: string
  value: string
  error: string
  onChange: (result: { value: string; error: string }) => void
  validate: (value: string) => string
  id?: string
  containerClassName?: string
  componentClass?: string
  isValidDate?: (current: Moment) => boolean
  viewMode?: string
  defaultValue?: string | Moment
  renderDay?: (
    props: unknown,
    currentDate: Moment,
    selectedDate: Moment,
  ) => React.ReactNode
  inputClassName?: string
  customisedDateFormat?: string
  placeholder?: string
  readOnly?: boolean
  before?: string
  after?: string
}

/**
 * Custom date input for Rental Reference Last Inspected Date
 * Extends BaseInputDate with month/year format support (MM/YYYY, M/YYYY, etc.)
 * ONLY used in rental reference form - do not use elsewhere
 */
class RentalReferenceInputDate extends BaseInputDate {
  // @ts-expect-error - Overrides BaseInputDate.props to add proper typing
  props!: RentalReferenceInputDateProps

  // @ts-expect-error - Overrides BaseInputDate.checkMoment with month/year format support
  checkMoment = (date: string): void => {
    const dateString = date.toString()

    // Handle empty input - delegate to validator
    if (!dateString || dateString.trim() === '') {
      const value = ''
      const error = this.props.validate(value)
      this.props.onChange({ value, error })
      return
    }

    // Formats specific to rental reference month/year input
    const formats = [
      dateTimeHelpers.DATE_WITH_SLASH, // DD/MM/YYYY
      'DD-MM-YYYY',
      'D/M/YYYY',
      'D-M-YYYY',
      'MM/YYYY', // Month/year formats
      'M/YYYY',
      'MM-YYYY',
      'M-YYYY',
      'MMM YYYY',
      'MMMM YYYY',
      helpers.dateWithDash, // YYYY-MM-DD
    ]

    // Try each format with strict parsing
    let dateMomentObject: Moment | null = null
    let isValid = false

    for (const format of formats) {
      const testMoment = moment(dateString, format, true)
      if (testMoment.isValid()) {
        const parsedMonth = testMoment.month() + 1
        const parsedDay = testMoment.date()
        const parsedYear = testMoment.year()

        if (parsedMonth < 1 || parsedMonth > 12) continue
        if (parsedDay < 1 || parsedDay > testMoment.daysInMonth()) continue
        if (parsedYear < 1900 || parsedYear > 2100) continue

        dateMomentObject = testMoment
        isValid = true
        break
      }
    }

    if (isValid && dateMomentObject) {
      this.validateDate(dateMomentObject)
    } else {
      const error = 'Invalid date'
      const value = ''
      this.props.onChange({ value, error })
    }
  }

  handleBlur = (): void => {
    const { value, customisedDateFormat = helpers.dateWithDash } = this.props
    // On blur, re-validate the current value to ensure it's valid
    if (value && value !== '') {
      const momentValue = moment(value, customisedDateFormat, true)
      if (!momentValue.isValid()) {
        const error = 'Invalid date'
        const emptyValue = ''
        this.props.onChange({ value: emptyValue, error })
      }
    }
  }
}

export default RentalReferenceInputDate
